import { FC, useState } from 'react';

import { Button } from '@app/shared/ui/button';
import { ProductDto } from '@app/types/product.type';
import { useTranslation } from '@app/app/i18n';
import { analytics } from '@app/app/analytics';
import { PlatformName } from '@app/app/analytics/types';
import { StandardTooltip } from '@app/shared/ui/standard-tooltip';
import { TooltipProvider } from '@app/shared/ui/tooltip';
import { ErrorCircleIcon, GuardIcon } from '@app/components/ui/icons';
import { cvaIcon } from '@app/shared/ui/elements';

import clsx from 'clsx';

import {
  cvaSelectCardDescriptionWithGuard,
  cvaRadioButtons,
  cvaRadioButtonText,
  cvaRadioButton,
  cvaRadioButtonContent,
  cvaRadioButtonIcon,
  cvaRadioButtonAiTokensText,
} from '@app/widgets/pricing-cards/ui/pricing-card-styles';

type PlansSelectorCompactProps = {
  products: {
    starter?: { monthly?: ProductDto; yearly?: ProductDto };
    basic?: { monthly?: ProductDto; yearly?: ProductDto };
    expert?: { monthly?: ProductDto; yearly?: ProductDto };
  };
  onSelectPlan: (product: ProductDto) => void;
  isLoading: boolean;
  footerSlot?: React.ReactNode;
};

type RadioButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  text: string;
  price: string;
  priceDescription: string;
  isActive: boolean;
  aiTokensText?: string;
  aiTokensTooltip?: string;
};

const RadioButton = ({
  text,
  price,
  isActive,
  aiTokensText,
  aiTokensTooltip,
  ...buttonProps
}: RadioButtonProps) => {
  return (
    <button
      className={clsx(cvaRadioButton({ isActive: true }), 'border-0 !px-0 py-[5px]')}
      {...buttonProps}
    >
      <span className={clsx(cvaRadioButtonContent(), 'inline-flex w-full')}>
        <span className="flex items-center gap-2 flex-grow">
          <span className="flex flex-col flex-grow">
            <span className="inline-flex items-center gap-2">
              <span className={clsx(cvaRadioButtonIcon({ isActive }), '!w-5 !h-5 self-start')} />
              <span className={clsx(cvaRadioButtonText(), 'inline-flex justify-between w-full')}>
                <span className="font-inter font-bold text-[18px] leading-[22px] tracking-[0px] mb-2">
                  {text}
                </span>{' '}
                <span className="font-inter font-black text-[24px] leading-[28px] tracking-[0px] mb-0 text-right">
                  {price}
                </span>
              </span>
            </span>
            {aiTokensText && (
              <>
                {aiTokensTooltip ? (
                  <TooltipProvider>
                    <StandardTooltip
                      text={aiTokensTooltip}
                      tooltipProps={{ delayDuration: 0 }}
                      contentProps={{ side: 'bottom' }}
                      contentClassName="bg-main_white text-main_black border-0"
                      arrowClassName="hidden"
                      triggerClassName="w-full"
                    >
                      <span
                        className={clsx(
                          cvaRadioButtonAiTokensText(),
                          'mt-1 flex items-center gap-1 cursor-help text-xs',
                        )}
                      >
                        {aiTokensText}
                        <ErrorCircleIcon className={cvaIcon({ size: 'small' })} />
                      </span>
                    </StandardTooltip>
                  </TooltipProvider>
                ) : (
                  <span
                    className={clsx(
                      cvaRadioButtonAiTokensText(),
                      'mt-1 flex items-center gap-1 text-xs',
                    )}
                  >
                    {aiTokensText}
                  </span>
                )}
              </>
            )}
          </span>
        </span>
      </span>
    </button>
  );
};

export const PlansSelectorCompact: FC<PlansSelectorCompactProps> = ({
  products,
  onSelectPlan,
  isLoading,
  footerSlot,
}) => {
  const { t } = useTranslation('pricing');
  const [selectedBilling, setSelectedBilling] = useState<'yearly' | 'monthly'>('yearly');
  const [selectedPlan, setSelectedPlan] = useState<'starter' | 'basic' | 'expert'>('starter');

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-1/3 mb-4" />
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 bg-gray-200 rounded" />
          ))}
        </div>
      </div>
    );
  }

  const getSelectedProduct = (): ProductDto | null => {
    const planProducts = products[selectedPlan];
    if (!planProducts) return null;

    const product = selectedBilling === 'yearly' ? planProducts.yearly : planProducts.monthly;
    return product || null;
  };

  const getAiTokensText = (
    product: ProductDto | undefined,
    planName: string,
  ): string | undefined => {
    if (!product) return undefined;

    const monthlyTokens = product.included_ai_token_count;

    if (selectedBilling === 'yearly') {
      const totalTokens = monthlyTokens * 12;
      return t('planCard.aiTokensYearly', {
        monthly: monthlyTokens,
        total: totalTokens.toLocaleString(),
      });
    } else {
      return t('planCard.aiTokensMonthly', {
        count: monthlyTokens,
        plan: planName.toLowerCase(),
      });
    }
  };

  const getAiTokensTooltip = (planName: string): string | undefined => {
    const tooltipKey = `planCard.presentationsTooltip.${planName.toLowerCase()}`;
    return t(tooltipKey);
  };

  const handleUpgrade = () => {
    const product = getSelectedProduct();
    if (!product) return;

    analytics.emitEvent('click_plan', {
      [PlatformName.GTM]: {
        plan: `${selectedBilling}_${selectedPlan}` as const,
      },
    });
    onSelectPlan(product);
  };

  return (
    <div className="flex flex-col gap-3 pt-6 pr-5 pb-6 pl-5 border border-dashed border-general_text_MII_secondary_i rounded-lg h-full">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-inter font-bold text-[18px] leading-[22px] tracking-[0px] text-general_text_MII_default">
          {t('planCard.chooseDuration')}
        </h3>
        {/* Switcher Monthly/Yearly */}
        <div className="flex items-center rounded-[50px] border border-solid border-general_MI_500_default">
          <button
            onClick={() => setSelectedBilling('monthly')}
            className={clsx(
              'px-2 py-1 transition-all rounded-[50px]',
              selectedBilling === 'monthly'
                ? 'bg-general_MI_500_default text-white'
                : 'text-white hover:opacity-100',
            )}
          >
            {t('planCard.monthlyButton')}
          </button>
          <button
            onClick={() => setSelectedBilling('yearly')}
            className={clsx(
              'px-2 py-1 transition-all relative rounded-[50px]',
              selectedBilling === 'yearly' ? 'bg-general_MI_500_default text-white' : 'text-white',
            )}
          >
            {t('planCard.yearlyButton')}
            <span className="absolute -top-2 -right-[25%] text-[10px] bg-secondary_warning_SI_500_warning text-main_white px-1 rounded font-bold size-[10px] w-auto h-auto">
              Up to -70%
            </span>
          </button>
        </div>
      </div>

      <div className={cvaRadioButtons()}>
        {products.starter && (
          <RadioButton
            text="Starter"
            price={`${
              (selectedBilling === 'yearly' ? products.starter.yearly : products.starter.monthly)
                ?.price.currency_sign || '$'
            } ${
              (selectedBilling === 'yearly' ? products.starter.yearly : products.starter.monthly)
                ?.price.amount || '0'
            }`}
            priceDescription={
              selectedBilling === 'yearly' ? t('planCard.perYear') : t('planCard.perMonth')
            }
            isActive={selectedPlan === 'starter'}
            onClick={() => setSelectedPlan('starter')}
            aiTokensText={getAiTokensText(
              selectedBilling === 'yearly' ? products.starter.yearly : products.starter.monthly,
              'starter',
            )}
            aiTokensTooltip={getAiTokensTooltip('Starter')}
          />
        )}

        {products.basic && (
          <RadioButton
            text="Basic"
            price={`${
              (selectedBilling === 'yearly' ? products.basic.yearly : products.basic.monthly)?.price
                .currency_sign || '$'
            } ${
              (selectedBilling === 'yearly' ? products.basic.yearly : products.basic.monthly)?.price
                .amount || '0'
            }`}
            priceDescription={
              selectedBilling === 'yearly' ? t('planCard.perYear') : t('planCard.perMonth')
            }
            isActive={selectedPlan === 'basic'}
            onClick={() => setSelectedPlan('basic')}
            aiTokensText={getAiTokensText(
              selectedBilling === 'yearly' ? products.basic.yearly : products.basic.monthly,
              'basic',
            )}
            aiTokensTooltip={getAiTokensTooltip('Basic')}
          />
        )}

        {products.expert && (
          <RadioButton
            text="Expert"
            price={`${
              (selectedBilling === 'yearly' ? products.expert.yearly : products.expert.monthly)
                ?.price.currency_sign || '$'
            } ${
              (selectedBilling === 'yearly' ? products.expert.yearly : products.expert.monthly)
                ?.price.amount || '0'
            }`}
            priceDescription={
              selectedBilling === 'yearly' ? t('planCard.perYear') : t('planCard.perMonth')
            }
            isActive={selectedPlan === 'expert'}
            onClick={() => setSelectedPlan('expert')}
            aiTokensText={getAiTokensText(
              selectedBilling === 'yearly' ? products.expert.yearly : products.expert.monthly,
              'expert',
            )}
            aiTokensTooltip={getAiTokensTooltip('Expert')}
          />
        )}
      </div>

      <Button
        onClick={handleUpgrade}
        className="w-full h-10 rounded-lg flex items-center justify-center gap-2 bg-general_MI_500_default text-white font-semibold hover:bg-general_MI_600_hover transition-colors"
      >
        {t('planCard.upgradeNowButton')}
      </Button>

      <div className={clsx(cvaSelectCardDescriptionWithGuard(), 'border-0 !mt-0')}>
        <GuardIcon
          className={clsx(cvaIcon({ size: 'large' }), 'text-secondary_warning_SI_500_warning')}
        />
        <p
          className="font-inter font-bold text-[13px] leading-[16px] tracking-[0px] text-secondary_warning_SI_500_warning"
          dangerouslySetInnerHTML={{ __html: t('planCard.cancelAnytime') }}
        />
      </div>

      {footerSlot}
    </div>
  );
};
